import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import SCHEDULE_OVERRIDE from "../api/services/SCHEDULE_OVERRIDE";
import styles from "../styles/_screens/scheduleOverride.module.scss";

const HEB_DAY_NAMES = [
  "ראשון",
  "שני",
  "שלישי",
  "רביעי",
  "חמישי",
  "שישי",
  "שבת",
];

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getDayName(dateStr) {
  const d = new Date(dateStr);
  return HEB_DAY_NAMES[d.getDay()];
}

function toDateInputValue(dateStr) {
  // Ensure YYYY-MM-DD for <input type="date">
  if (!dateStr) return "";
  return dateStr.slice(0, 10);
}

function getTodayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Generate an array of YYYY-MM-DD strings between start and end (inclusive)
function getDateRange(start, end) {
  const dates = [];
  const s = new Date(start);
  const e = new Date(end);
  while (s <= e) {
    const y = s.getFullYear();
    const m = String(s.getMonth() + 1).padStart(2, "0");
    const d = String(s.getDate()).padStart(2, "0");
    dates.push(`${y}-${m}-${d}`);
    s.setDate(s.getDate() + 1);
  }
  return dates;
}

export default function ScheduleOverride() {
  const employeeId = JSON.parse(localStorage.getItem("user_id") || '""');

  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isRange, setIsRange] = useState(false);
  const [dateFrom, setDateFrom] = useState(getTodayStr());
  const [dateTo, setDateTo] = useState(getTodayStr());
  const [timing, setTiming] = useState([
    { start_time: "09:00", end_time: "17:00" },
  ]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null); // {type, text}

  // Conflict state
  const [conflicts, setConflicts] = useState(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Fetch ────────────────────────────────────────────────────
  const fetchOverrides = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await SCHEDULE_OVERRIDE.getAll(employeeId);
      const data = res?.data || res;
      const list = Array.isArray(data) ? data : [];
      setOverrides(list);
    } catch (err) {
      console.log("fetchOverrides error:", err);
      toast.error("שגיאה בטעינת שינויים זמניים");
    }
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  // ── Editor open/close ────────────────────────────────────────
  const openEditor = () => {
    setIsRange(false);
    setDateFrom(getTodayStr());
    setDateTo(getTodayStr());
    setTiming([{ start_time: "09:00", end_time: "17:00" }]);
    setComment("");
    setStatusMsg(null);
    setConflicts(null);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setStatusMsg(null);
    setConflicts(null);
  };

  // ── Timing helpers ───────────────────────────────────────────
  const updateTiming = (idx, field, value) => {
    setTiming((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  };

  const addTimingRow = () => {
    setTiming((prev) => [...prev, { start_time: "09:00", end_time: "17:00" }]);
  };

  const removeTimingRow = (idx) => {
    setTiming((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Validate ─────────────────────────────────────────────────
  const validateTiming = () => {
    if (!dateFrom) return "יש לבחור תאריך";
    if (isRange && !dateTo) return "יש לבחור תאריך סיום";
    if (isRange && dateTo < dateFrom) return "תאריך הסיום חייב להיות אחרי תאריך ההתחלה";
    for (const t of timing) {
      if (!t.start_time || !t.end_time) return "יש למלא את כל זמני הקבלה";
      const [sh, sm] = t.start_time.split(":").map(Number);
      const [eh, em] = t.end_time.split(":").map(Number);
      if (sh * 60 + sm >= eh * 60 + em)
        return "שעת הסיום חייבת להיות אחרי שעת ההתחלה";
    }
    return null;
  };

  // ── Save ─────────────────────────────────────────────────────
  const performSave = async () => {
    setSaving(true);
    setStatusMsg({ type: "info", text: "שומר..." });
    try {
      const dates = isRange
        ? getDateRange(dateFrom, dateTo)
        : [dateFrom];

      let result;
      if (dates.length === 1) {
        result = await SCHEDULE_OVERRIDE.create({
          employeeId,
          date: dates[0],
          timing,
          comment,
        });
      } else {
        result = await SCHEDULE_OVERRIDE.createRange({
          employeeId,
          dates,
          timing,
          comment,
        });
      }

      const resData = result?.data || result;
      setSaving(false);

      if (resData) {
        closeEditor();
        fetchOverrides();
        toast.success("שעות הקבלה החד-פעמיות נשמרו");
      } else {
        setStatusMsg({
          type: "error",
          text: "השמירה נכשלה. בדקו שהשרת פועל.",
        });
      }
    } catch (e) {
      setSaving(false);
      setStatusMsg({
        type: "error",
        text: "שגיאה: " + (e?.response?.data?.message || e?.message || "שגיאה לא צפויה"),
      });
    }
  };

  const onSave = async () => {
    setStatusMsg(null);
    const err = validateTiming();
    if (err) {
      setStatusMsg({ type: "error", text: err });
      return;
    }

    const dates = isRange ? getDateRange(dateFrom, dateTo) : [dateFrom];

    // Check conflicts first
    setSaving(true);
    setStatusMsg({ type: "info", text: "בודק התנגשויות עם תורים קיימים..." });
    try {
      const conflictRes = await SCHEDULE_OVERRIDE.checkConflicts({
        employeeId,
        dates,
        timing,
      });
      const found = conflictRes?.data || conflictRes;
      setSaving(false);

      if (Array.isArray(found) && found.length > 0) {
        setStatusMsg(null);
        setConflicts(found);
        return;
      }
      await performSave();
    } catch (e) {
      setSaving(false);
      setStatusMsg({
        type: "error",
        text: "שגיאה בבדיקת התנגשויות: " + (e?.message || ""),
      });
    }
  };

  // ── Delete ───────────────────────────────────────────────────
  const confirmDelete = (id) => setDeleteTarget(id);

  const handleDelete = async () => {
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await SCHEDULE_OVERRIDE.remove(id);
      toast.success("נמחק בהצלחה");
      fetchOverrides();
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה במחיקה");
    }
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerRight}>
            <h1 className={styles.pageTitle}>שינוי זמני קבלת קהל</h1>
            <p className={styles.pageSubtitle}>
              הגדירו שעות קבלה שונות ליום אחד או טווח תאריכים מבלי לשנות את
              הלו&quot;ז הקבוע
            </p>
          </div>
          <button className={styles.createBtn} onClick={openEditor}>
            + הוספת שינוי זמני
          </button>
        </div>

        <p className={styles.helpText}>
          השינויים הזמניים מאפשרים לשנות את שעות הקבלה ליום ספציפי או טווח
          תאריכים, מבלי לגעת בלוח הזמנים הקבוע שלכם.
        </p>

        {/* Content */}
        {loading ? (
          <div className={styles.loaderWrap}>
            <Loader />
          </div>
        ) : overrides.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>&#128197;</span>
            <p className={styles.emptyTitle}>אין שינויי שעות פעילים</p>
            <p className={styles.emptySub}>
              לחצו על &quot;הוספת שינוי זמני&quot; כדי להתחיל
            </p>
          </div>
        ) : (
          <div className={styles.cardList}>
            {overrides.map((item) => (
              <div key={item._id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardDate}>
                    יום {getDayName(item.date)}, {formatDate(item.date)}
                  </span>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => confirmDelete(item._id)}
                    title="מחיקה"
                  >
                    &#128465;
                  </button>
                </div>
                <div className={styles.cardBody}>
                  {(item.timing || []).map((t, i) => (
                    <div key={i} className={styles.timeRow}>
                      <span className={styles.timeIcon}>&#128338;</span>
                      <span>
                        {t.start_time} - {t.end_time}
                      </span>
                    </div>
                  ))}
                  {item.comment && (
                    <span className={styles.commentText}>{item.comment}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Editor modal ───────────────────────────────────────── */}
      {editorOpen && (
        <div className={styles.modalOverlay} onClick={closeEditor}>
          <div
            className={styles.modalSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>שעות קבלה חד-פעמיות</h2>
              <button className={styles.modalCloseBtn} onClick={closeEditor}>
                &times;
              </button>
            </div>

            {/* Date selection */}
            <div className={styles.fieldGroup}>
              <p className={styles.fieldLabel}>תאריך</p>
              <div className={styles.dateInputRow}>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={toDateInputValue(dateFrom)}
                  min={getTodayStr()}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                {isRange && (
                  <>
                    <span className={styles.rangeSep}>עד</span>
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={toDateInputValue(dateTo)}
                      min={dateFrom || getTodayStr()}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </>
                )}
                <button
                  className={
                    isRange ? styles.rangeToggleActive : styles.rangeToggle
                  }
                  onClick={() => {
                    setIsRange(!isRange);
                    if (!isRange) setDateTo(dateFrom);
                  }}
                >
                  {isRange ? "יום בודד" : "טווח תאריכים"}
                </button>
              </div>
            </div>

            {/* Timing */}
            <div className={styles.fieldGroup}>
              <p className={styles.fieldLabel}>שעות קבלה ביום</p>
              {timing.map((t, idx) => (
                <div key={idx} className={styles.timingRow}>
                  <div className={styles.timingField}>
                    <span className={styles.timingLabel}>משעה</span>
                    <input
                      type="time"
                      className={styles.timeInput}
                      value={t.start_time}
                      onChange={(e) =>
                        updateTiming(idx, "start_time", e.target.value)
                      }
                    />
                  </div>
                  <span className={styles.timingDash}>-</span>
                  <div className={styles.timingField}>
                    <span className={styles.timingLabel}>עד שעה</span>
                    <input
                      type="time"
                      className={styles.timeInput}
                      value={t.end_time}
                      onChange={(e) =>
                        updateTiming(idx, "end_time", e.target.value)
                      }
                    />
                  </div>
                  {timing.length > 1 && (
                    <button
                      className={styles.removeTimingBtn}
                      onClick={() => removeTimingRow(idx)}
                      title="הסר חלון זמן"
                    >
                      &#128465;
                    </button>
                  )}
                </div>
              ))}
              <button className={styles.addTimingBtn} onClick={addTimingRow}>
                + הוסף חלון זמן נוסף
              </button>
            </div>

            {/* Comment */}
            <div className={styles.fieldGroup}>
              <p className={styles.fieldLabel}>הערה (אופציונלי)</p>
              <input
                type="text"
                className={styles.commentInput}
                placeholder="למשל: יום קצר בגלל חג..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            {/* Status message */}
            {statusMsg && (
              <div
                className={
                  statusMsg.type === "error" ? styles.statusError : styles.statusInfo
                }
              >
                {statusMsg.text}
              </div>
            )}

            {/* Actions */}
            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={closeEditor}
                disabled={saving}
              >
                ביטול
              </button>
              <button
                className={styles.saveBtn}
                onClick={onSave}
                disabled={saving}
              >
                {saving ? "שומר..." : "שמירה"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Conflict overlay ───────────────────────────────────── */}
      {conflicts && (
        <div
          className={styles.conflictOverlay}
          onClick={() => setConflicts(null)}
        >
          <div
            className={styles.conflictSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <span className={styles.conflictIcon}>&#9888;</span>
                אזהרה: יש תורים קיימים
              </h2>
            </div>
            <p className={styles.conflictText}>
              נמצאו {conflicts.length} תורים קיימים מחוץ לשעות החדשות שהגדרתם:
            </p>
            <div className={styles.conflictList}>
              {conflicts.map((c, i) => (
                <div key={i} className={styles.conflictRow}>
                  &#8226; {formatDate(c.date)} {c.timeSlot} &mdash; {c.userName}{" "}
                  ({c.serviceName})
                </div>
              ))}
            </div>
            <p className={styles.conflictWarning}>
              התורים האלו לא יימחקו אוטומטית. מומלץ ליצור קשר עם הלקוחות.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setConflicts(null)}
                disabled={saving}
              >
                ביטול
              </button>
              <button
                className={styles.saveBtn}
                onClick={performSave}
                disabled={saving}
              >
                {saving ? "שומר..." : "שמור בכל זאת"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ─────────────────────────────────────── */}
      {deleteTarget && (
        <div
          className={styles.confirmOverlay}
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className={styles.confirmSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <p className={styles.confirmTitle}>מחיקת שינוי זמני</p>
            <p className={styles.confirmText}>האם למחוק את שינוי השעות הזה?</p>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmCancelBtn}
                onClick={() => setDeleteTarget(null)}
              >
                ביטול
              </button>
              <button className={styles.confirmDeleteBtn} onClick={handleDelete}>
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
